import React,{useCallback,useEffect,useState} from 'react';
import { Layout } from 'antd';
import { options, NAV_GROUPS } from './options';
import Scrollbars from '@iso/components/utility/customScrollBar';
import Menu from '@iso/components/uielements/menu';
import appActions from '@iso/redux/app/actions';
import Logo from '@iso/components/utility/logo';
import SidebarWrapper from './Sidebar.styles';
import SubscriptionModal from "../../containers/Pages/Property/SubscriptionModal";
import SidebarMenu from './SidebarMenu';
import { hiddenKeys, hasPlanAccess } from './hiddenKeys';
import siteConfig from '@iso/config/site.config';
import { propertyService,subscriptionService } from '@iso/services';
import { useSelector, useDispatch } from 'react-redux';
import { commonHelper } from '@iso/lib/helpers/commonHelper';
import { sensorService } from '../../services/sensor.service';
import { propertyRoleService } from '../../services/propertyRole.service';
import { LeftOutlined } from '@ant-design/icons';

const { Sider } = Layout;
const {
  toggleOpenDrawer,
  changeOpenKeys,
  changeCurrent,
  toggleCollapsed,
} = appActions;

export default function Sidebar() {
  const dispatch = useDispatch();
  
  const { toggleCollapsed, toggleChangeText } = appActions;
  const [stripePlan, setStripePlan] = useState(null);
  const [collapseIcon, setCollapseIcon] = useState(false);
  const [isSectionVisible, setIsSectionVisible] = useState(true);
  const [openSubscription, setOpenSubscription] = useState(false);
  const [hiddenItems, setHiddenItems] = useState([]);
  // Group dropdowns (Operations / Management / Farm / ...) default to expanded
  // on every load; collapsing lasts only for the current session.
  const [navOpenKeys, setNavOpenKeys] = useState(
    NAV_GROUPS.map((g) => `grp-${g.key}`)
  );
  const hiddenItemsCacheRef = React.useRef({});

  const {
    view,
    openKeys,
    collapsed,
    openDrawer,
    current,
    height,
  } = useSelector(state => state.App);
  const customizedTheme = useSelector(
    state => state.ThemeSwitcher.sidebarTheme
  );
  const properties = useSelector((state) => state.property.properties);
  const activeId = useSelector((state) => state.property.activePropertyId);
  const currentUser = useSelector((state) => state.user.user);
  const activePropertySubscription = useSelector((state) => state.subscription.activePropertySubscription);
  // const activePermission = useSelector((state) => state.permission.activePermission);

  const activePropertyId = useSelector((state) => state.property.activePropertyId);

  const activePermission = useSelector(
    (state) => state.permission.activePermission
  );
  const activeProperty = commonHelper.getActiveProperty();

  const handleToggle = React.useCallback(() => {
    dispatch(toggleCollapsed());
    dispatch(toggleChangeText());
    setCollapseIcon(true);
    setIsSectionVisible(!isSectionVisible);
  }, [dispatch,isSectionVisible]);

  const prevActiveIdRef = React.useRef();
  useEffect(() => {
    // Skip if activeId hasn't actually changed (avoids duplicate calls during
    // PrivateLayout property restoration which sets activeId from undefined→value)
    if (!activeId || (prevActiveIdRef.current === activeId)) return;
    prevActiveIdRef.current = activeId;
    // Use cached result if available for this property
    if (hiddenItemsCacheRef.current[activeId]) {
      setHiddenItems(hiddenItemsCacheRef.current[activeId]);
      return;
    }
    resolveHiddenItems();
  }, [activeId, currentUser?.id]);

  useEffect(() => {
    const handleMenuConfigUpdated = () => resolveHiddenItems();
    window.addEventListener('menuConfigUpdated', handleMenuConfigUpdated);
    return () => window.removeEventListener('menuConfigUpdated', handleMenuConfigUpdated);
  }, []);
  
  function handleClick(e) {
    dispatch(changeCurrent([e.key]));
    if (view === 'MobileView') {
      setTimeout(() => {
        dispatch(toggleCollapsed());
        // dispatch(toggleOpenDrawer());
      }, 100);

      // clearTimeout(timer);
    }
  }
  function onOpenChange(newOpenKeys) {
    const latestOpenKey = newOpenKeys.find(
      key => !(openKeys.indexOf(key) > -1)
    );
    const latestCloseKey = openKeys.find(
      key => !(newOpenKeys.indexOf(key) > -1)
    );
    let nextOpenKeys = [];
    if (latestOpenKey) {
      nextOpenKeys = getAncestorKeys(latestOpenKey).concat(latestOpenKey);
    }
    if (latestCloseKey) {
      nextOpenKeys = getAncestorKeys(latestCloseKey);
    }
    dispatch(changeOpenKeys(nextOpenKeys));
  }
  const getAncestorKeys = key => {
    const map = {
      sub3: ['sub2'],
    };
    return map[key] || [];
  };
  const handleMailto = () => {
    const mailtoLink = 'mailto:support@airagri.com.au';
    window.location.href = mailtoLink;
  }
  const isCollapsed = collapsed && !openDrawer;
  const mode = isCollapsed === true ? 'vertical' : 'inline';
  const onMouseEnter = event => {
    if (collapsed && openDrawer === false) {
      dispatch(toggleOpenDrawer());
    }
    return;
  };
  const onMouseLeave = () => {
    if (collapsed && openDrawer === true) {
      dispatch(toggleOpenDrawer());
    }
    return;
  };
  const styling = {
    backgroundColor: customizedTheme.backgroundColor,
  };
  const submenuStyle = {
    backgroundColor: 'rgba(0,0,0,0.3)',
    color: customizedTheme.textColor,
  };
  const submenuColor = {
    color: customizedTheme.textColor,
  };
  const propertyOwnerRoutes = ['manage-inspection'];
  // Owner/admin-only modules: hidden for other roles here, enforced
  // server-side by their controllers.
  const propertyOwnerAdminRoutes = ['manage-visitor', 'contractors'];
  const handleOpenSubscriptionModal = () => {
    setOpenSubscription(true);
  };
  const cancelUpgradeModal = () => {
    setOpenSubscription(false);
  };

  const resolveHiddenItems = async () => {
    if (!activeProperty || !activeProperty.id) {
      setHiddenItems(['corporate-reporting', 'training-module']);
      return;
    }

    const propId = activeProperty.id;
    let hidden = [];

    // Run all 3 checks in parallel instead of sequentially
    const [sensorResult, menuResult, roleResult] = await Promise.allSettled([
      sensorService.getList({ property_id: propId }),
      propertyService.getMenuConfig(propId),
      currentUser?.id
        ? propertyRoleService.checkUserRole(propId, currentUser.id, 'group_safety_manager')
        : Promise.resolve(null),
    ]);

    if (sensorResult.status === 'fulfilled' && sensorResult.value.sensors.length === 0) {
      hidden.push('manage-sensors');
    }

    if (menuResult.status === 'fulfilled') {
      const res = menuResult.value;
      if ((res.code === '0000' || res.code === 200) && res.menu_config) {
        hidden = [...hidden, ...res.menu_config];
      }
    }

    if (!currentUser?.id || roleResult.status === 'rejected' || !roleResult.value?.has_role) {
      hidden.push('corporate-reporting');
    }

    // Cache and set
    hiddenItemsCacheRef.current[propId] = hidden;
    setHiddenItems(hidden);
  };

  const subscriptionModalSaved = () => {
    cancelUpgradeModal();
    window.location.reload();
  };

  return (
    <SidebarWrapper>
      <Sider
        trigger={null}
        collapsible={true}
        collapsed={isCollapsed}
        width={240}
        className="isomorphicSidebar"
        // onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        style={styling}
      >
        <Logo collapsed={isCollapsed} />
        <Scrollbars style={{ height: height - 70 - 48 }}>
          <Menu
            onClick={handleClick}
            theme="dark"
            className="isoDashboardMenu"
            mode={mode}
            inlineIndent={0}
            openKeys={isCollapsed ? [] : navOpenKeys}
            selectedKeys={current}
            onOpenChange={setNavOpenKeys}
          >
            {(() => {
              const isVisible = (o) =>
                (!propertyOwnerRoutes.includes(o.key) || activePermission === 'owner')
                && (!propertyOwnerAdminRoutes.includes(o.key) || ['owner', 'admin'].includes(activePermission))
                && !hiddenItems.includes(o.key)
                // Drop pages the plan doesn't include here, not just in
                // SidebarMenu, so a group with none left isn't shown empty.
                && (!hiddenKeys().includes(o.key) || hasPlanAccess(o.key, activePropertySubscription));
              const visible = options(properties, activeId, currentUser.is_system_admin).filter(isVisible);
              const renderItem = (singleOption) => (
                <SidebarMenu
                  key={singleOption.key}
                  submenuStyle={submenuStyle}
                  submenuColor={submenuColor}
                  singleOption={singleOption}
                />
              );
              return (
                <>
                  {visible.filter((o) => !o.group).map(renderItem)}
                  {NAV_GROUPS.map((g) => {
                    const items = visible.filter((o) => o.group === g.key);
                    if (!items.length) return null;
                    return (
                      <Menu.SubMenu
                        key={`grp-${g.key}`}
                        className="nav-group"
                        title={g.label}
                      >
                        {items.map(renderItem)}
                      </Menu.SubMenu>
                    );
                  })}
                </>
              );
            })()}
          </Menu>
          {!activeProperty || activeProperty?.subscription_label === process.env.REACT_APP_STRIPE_PLAN_GOLD_ANNUAL || activeProperty?.subscription_label === process.env.REACT_APP_STRIPE_PLAN_GOLD ? '' :
           !collapsed && (
            <div className='upgradeCust'>
              <div className="mailmainBox"><img src={process.env.PUBLIC_URL + "./images/icons/Icon.svg"} className="arrowImg" alt="sdfsdf"></img></div>
              <div className="subDivBox">
                <div className="infoBox">
                  Upgrade to <span className="pro">{activeProperty?.subscription_name == "Bronze - Free" ?"Gold" :(activeProperty?.subscription_name == "Silver" ?"Gold" :'')}</span> for premium
                  features
                </div>
                <button
                  className="infoBtn"
                  onClick={handleOpenSubscriptionModal}
                >
                  Upgrade
                </button>
              </div>
            </div>
          )
          }
          {
          !isCollapsed && 
            <div className="footer-text" style={{color: '#B0B0B0', padding: '1.5rem'}}>{siteConfig.footerText}</div>
          }
        </Scrollbars> 
        <div
          className="collapseBar"
          onClick={handleToggle}
          title={isSectionVisible ? 'Collapse menu' : 'Expand menu'}
        >
          <LeftOutlined style={{ fontSize: "14px" }} rotate={isSectionVisible ? 0 : 180} />
        </div>
      </Sider>
      {openSubscription ?
      <SubscriptionModal
        openSubscription={openSubscription}
        setOpenSubscription = {setOpenSubscription}
        property={activeProperty}
        cancelModal={cancelUpgradeModal}
        onSaved={subscriptionModalSaved}
      />:''}
    </SidebarWrapper>
  );
}